import { useState, useRef, useEffect } from 'react';
import type { PressedSpecimen, LocalTrayState } from '../lib/types';
import { updateSpreadLayout, getPressedSpecimens } from '../lib/store';

interface Props {
  state: LocalTrayState;
  onUpdate?: () => void;
}

export function SpreadCanvas({ state, onUpdate }: Props) {
  const [specimens, setSpecimens] = useState<PressedSpecimen[]>(() => getPressedSpecimens());
  const [positions, setPositions] = useState<Record<string, { x: number; y: number; rot: number }>>({});
  const [dragging, setDragging] = useState<string | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Load persisted layout
  useEffect(() => {
    const layout = state.layout?.layout || {};
    const initial: Record<string, any> = {};
    specimens.forEach((p, i) => {
      const key = p.tending.id;
      if (layout[key]) initial[key] = { ...layout[key], rot: layout[key].rot || (i % 3 - 1) * 3 };
      else initial[key] = { x: 60 + (i % 4) * 140, y: 70 + Math.floor(i / 4) * 110, rot: (i % 3 - 1) * 4 };
    });
    setPositions(initial);
  }, [state.layout, specimens.length]);

  // Keep specimens fresh when parent state changes
  useEffect(() => {
    setSpecimens(getPressedSpecimens());
  }, [state.tendings.length]);

  function startDrag(e: React.PointerEvent, id: string) {
    const rect = containerRef.current!.getBoundingClientRect();
    const pos = positions[id] || { x: 80, y: 80, rot: 0 };
    setOffset({ x: e.clientX - rect.left - pos.x, y: e.clientY - rect.top - pos.y });
    setDragging(id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const nx = Math.max(20, Math.min(rect.width - 140, e.clientX - rect.left - offset.x));
    const ny = Math.max(20, Math.min(rect.height - 90, e.clientY - rect.top - offset.y));
    const next = { ...positions, [dragging]: { ...positions[dragging], x: nx, y: ny } };
    setPositions(next);
  }

  function endDrag() {
    if (dragging) {
      updateSpreadLayout({ [dragging]: positions[dragging] });
      onUpdate?.();
    }
    setDragging(null);
  }

  function nudge(id: string, dx: number, dy: number) {
    const p = positions[id];
    if (!p) return;
    const next = { ...positions, [id]: { ...p, x: p.x + dx, y: p.y + dy } };
    setPositions(next);
    updateSpreadLayout({ [id]: next[id] });
    onUpdate?.();
  }

  function rotate(id: string, d: number) {
    const p = positions[id];
    if (!p) return;
    const next = { ...positions, [id]: { ...p, rot: (p.rot || 0) + d } };
    setPositions(next);
    updateSpreadLayout({ [id]: next[id] });
    onUpdate?.();
  }

  // Draw delicate connecting lines between some specimens (simple demo connections)
  const lines = specimens.length > 1 ? [
    [0, 1], [1, 2], [0, 3]
  ].filter(([a, b]) => specimens[a] && specimens[b]) : [];

  return (
    <div
      ref={containerRef}
      className="spread w-full h-[420px] md:h-[480px] rounded-2xl overflow-hidden relative select-none"
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      {/* delicate paper lines */}
      <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%">
        {lines.map(([a, b], idx) => {
          const pa = positions[specimens[a].tending.id];
          const pb = positions[specimens[b].tending.id];
          if (!pa || !pb) return null;
          return (
            <line
              key={idx}
              x1={pa.x + 64} y1={pa.y + 38}
              x2={pb.x + 64} y2={pb.y + 38}
              className="spread-line"
            />
          );
        })}
      </svg>

      {specimens.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-[var(--text-subtle)] text-sm">
          Press interpretations from the Tending Bench to grow the spread.
        </div>
      )}

      {specimens.map((p, idx) => {
        const key = p.tending.id;
        const pos = positions[key] || { x: 60 + idx * 40, y: 60 + idx * 30, rot: 0 };
        return (
          <div
            key={key}
            className="pressed-card absolute px-3 py-2.5 w-[198px] cursor-grab active:cursor-grabbing rounded-xl"
            style={{
              left: pos.x,
              top: pos.y,
              transform: `rotate(${pos.rot || 0}deg)`,
              zIndex: dragging === key ? 50 : 10 + idx,
            }}
            onPointerDown={(e) => startDrag(e, key)}
          >
            <div className="text-[11px] leading-snug pr-5">
              {p.tending.content}
            </div>

            <div className="meta mt-1.5 flex items-center justify-between">
              <span>— {p.member.tendril_name}</span>
              <span className="opacity-60">from {p.seedling.species}</span>
            </div>

            {p.aftertaste && p.aftertaste.shared_to_spread && (
              <div className="aftertaste mt-2 px-2 py-1 text-[10px] rounded">
                {p.aftertaste.recovery_note?.slice(0, 92)}{p.aftertaste.recovery_note && p.aftertaste.recovery_note.length > 92 ? '…' : ''}
              </div>
            )}

            {/* tiny controls while "in the spread" */}
            <div className="absolute top-1.5 right-1.5 flex gap-0.5 opacity-60 hover:opacity-100 text-[10px]">
              <button onClick={() => nudge(key, -6, 0)} className="px-0.5 hover:text-[var(--accent)]">←</button>
              <button onClick={() => nudge(key, 0, -6)} className="px-0.5 hover:text-[var(--accent)]">↑</button>
              <button onClick={() => nudge(key, 6, 0)} className="px-0.5 hover:text-[var(--accent)]">→</button>
              <button onClick={() => nudge(key, 0, 6)} className="px-0.5 hover:text-[var(--accent)]">↓</button>
              <button onClick={() => rotate(key, -2)} className="px-0.5 hover:text-[var(--lavender)]">↺</button>
              <button onClick={() => rotate(key, 2)} className="px-0.5 hover:text-[var(--lavender)]">↻</button>
            </div>
          </div>
        );
      })}

      <div className="absolute bottom-2 right-3 text-[9px] text-[var(--text-subtle)] no-print">
        drag the pressed readings • the ambiguity stays
      </div>
    </div>
  );
}
